type EnableErrorPayload = {
  message?: string;
  error?: {
    // Backend sends the Meta code as a string (e.g. "138000"); older responses used a number
    code?: number | string;
    providerResponse?: {
      error?: {
        code?: number | string;
        message?: string;
      };
    };
  };
};

export function getMetaCallingErrorCode(error: unknown): number | undefined {
  const data = (error as { data?: EnableErrorPayload })?.data;
  const code = Number(data?.error?.code ?? data?.error?.providerResponse?.error?.code);
  return Number.isFinite(code) && code > 0 ? code : undefined;
}

export function getWhatsAppEnableErrorMessage(
  error: unknown,
  t: (key: string) => string
): string {
  const data = (error as { data?: EnableErrorPayload })?.data;
  const metaCode = getMetaCallingErrorCode(error);
  const metaMessage = data?.error?.providerResponse?.error?.message;

  const apiMessage = data?.message ?? '';

  if (
    metaCode === 138000 ||
    apiMessage.toLowerCase().includes('calling api not enabled')
  ) {
    return t('WHATSAPP_CALLING_ENABLE_META_138000');
  }

  if (apiMessage) {
    return apiMessage;
  }

  if (metaMessage) {
    return metaMessage;
  }

  return t('WHATSAPP_CALLING_ENABLE_FAILED');
}

/** Meta calling error codes with their own translated, short message (en + ar). */
const CALLING_ERROR_KEYS: Record<number, string> = {
  138000: 'WHATSAPP_CALL_ERROR_138000',
  138006: 'WHATSAPP_CALL_ERROR_138006',
  138009: 'WHATSAPP_CALL_ERROR_138009',
};

/** Reads the Meta code from the payload (number or string), or from "(#138009)" in the message. */
function getCallingErrorCode(error: unknown): number | undefined {
  const data = (error as { data?: EnableErrorPayload })?.data;
  const fromMessage = `${data?.message ?? ''} ${data?.error?.providerResponse?.error?.message ?? ''}`.match(
    /#?(1380\d\d)/
  )?.[1];
  return getMetaCallingErrorCode(error) ?? (fromMessage ? Number(fromMessage) : undefined);
}

/**
 * Calling API errors (request permission, start call).
 * English: the backend's `message` as-is (it is already user-friendly and has no error code).
 * Other languages: the backend message is English-only, so known Meta codes get a translated
 * message and anything else gets the translated fallback. The error code is never shown.
 */
export function getWhatsAppApiErrorMessage(
  error: unknown,
  t: (key: string) => string,
  fallbackKey: string,
  lang?: string
): string {
  const data = (error as { data?: EnableErrorPayload })?.data;
  const apiMessage = data?.message ?? '';

  if (!lang || lang.startsWith('en')) {
    return apiMessage || t(fallbackKey);
  }

  const code = getCallingErrorCode(error);
  return t(code && CALLING_ERROR_KEYS[code] ? CALLING_ERROR_KEYS[code] : fallbackKey);
}
